import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '@/hooks';
import { useIsDark } from '@/hooks/useIsDark';
import { clearSession } from '@/redux/slices/sessionStateSlice';
import { setColorMode, DEFAULT_COLOR_MODE, type ColorMode } from '@/redux/slices/colorModeSlice';
import { useLogoutMutation, useGetTenantSettingsQuery } from '@/redux/internal-services/onboardingApi';
import { useGetMeQuery, useUpdateMeMutation } from '@/redux/internal-services/usersApi';
import { NotificationBell } from '@/components/Notifications';
import { IconButton } from '@/components/UI/IconButton';
import { LogOutIcon, MenuIcon, MonitorIcon, MoonIcon, SunIcon, UserIcon } from '@/components/UI/icons';
import { cn } from '@/utils/cn';

interface TopBarProps {
  /** Called when the hamburger button is tapped (mobile/tablet only). */
  onMenuClick?: () => void;
  /** @deprecated The header follows the theme tokens on every page now (it used to switch to a dark variant
   *  variant). Accepted so existing call sites keep compiling; ignored. */
  dark?: boolean;
  /** Ref callback for the actions slot rendered next to the profile menu — pages portal
   *  controls (e.g. the AI Chat view toggle) into it via TopBarSlotContext. */
  slotRef?: React.RefCallback<HTMLDivElement>;
}

const MODE_ICON: Record<ColorMode, React.FC<{ size?: number; className?: string }>> = {
  light: SunIcon,
  dark: MoonIcon,
  system: MonitorIcon,
};

const TopBar: React.FC<TopBarProps> = ({ onMenuClick, slotRef }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isDark = useIsDark();
  const userName = useAppSelector((state) => state.sessionState.userName);
  const userEmail = useAppSelector((state) => state.sessionState.userEmail);
  const [logout, { isLoading }] = useLogoutMutation();

  // Tenant branding shown on the LEFT of the header (desktop/web only). Platform roles are
  // cross-tenant, so they see no tenant name/logo. The company name comes from tenant settings;
  // the logo from the brand store (uploaded logo) with a lettered fallback badge.
  const roles = useAppSelector((s) => s.sessionState.roles);
  const isPlatformRole = roles.includes('platform_admin') || roles.includes('platform_support');
  const logoLightUrl = useAppSelector((s) => s.brand.logoLightUrl);
  const logoDarkUrl = useAppSelector((s) => s.brand.logoDarkUrl);
  const { data: tenantSettings } = useGetTenantSettingsQuery(undefined, { skip: isPlatformRole });
  const companyName = (
    (tenantSettings?.data as Record<string, unknown> | undefined)?.company_name as string | undefined
  )?.trim();
  // Each logo slot is for a background: the dark-background logo on the dark header, the light-background
  // one on the light header, falling back to whichever the tenant did upload.
  const tenantLogo = (isDark ? logoDarkUrl || logoLightUrl : logoLightUrl || logoDarkUrl || '').trim();

  const colorMode: ColorMode = useAppSelector((s) => s.colorMode?.mode ?? DEFAULT_COLOR_MODE);
  const { data: me } = useGetMeQuery();
  const [updateMe] = useUpdateMeMutation();

  const handleSetMode = (m: ColorMode) => {
    dispatch(setColorMode(m));
    updateMe({ theme_mode: m });
  };

  const cycleMode = () => {
    const next: Record<ColorMode, ColorMode> = { light: 'dark', dark: 'system', system: 'light' };
    handleSetMode(next[colorMode]);
  };

  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleEditProfile = () => {
    setOpen(false);
    navigate('/profile');
  };

  const handleLogout = async () => {
    setOpen(false);
    try {
      await logout().unwrap();
    } catch {
      // Ignore network/backend errors — local session is cleared regardless.
    } finally {
      dispatch(clearSession());
      navigate('/login', { replace: true });
    }
  };

  const ModeIcon = MODE_ICON[colorMode];
  const modeTitle =
    colorMode === 'light'
      ? 'Light mode — click for dark'
      : colorMode === 'dark'
        ? 'Dark mode — click for auto (follows device)'
        : 'Auto mode (follows device) — click for light';

  return (
    // Solid surface on purpose. This bar sits above the page (nothing scrolls under it), so a backdrop blur adds
    // nothing, and it would create a stacking context that traps the user-menu dropdown beneath the page content.
    <header className="flex h-[calc(3.5rem+env(safe-area-inset-top))] items-center justify-between border-b border-ui-border bg-ui-card px-3 pt-[env(safe-area-inset-top)] md:h-[calc(4rem+env(safe-area-inset-top))] md:px-6">
      {/* Left cluster: hamburger (mobile) + tenant company logo & name. Kept in one group so
          justify-between splits it cleanly against the right-hand controls. */}
      <div className="flex min-w-0 items-center gap-2">
        {/* Hamburger — mobile opens the drawer; desktop toggles sidebar collapse */}
        <IconButton label="Toggle navigation" onClick={onMenuClick}>
          <MenuIcon size={20} />
        </IconButton>

        {/* Tenant company logo + name — desktop only (hidden on mobile/tablet). Hidden for
            platform roles (cross-tenant) too. */}
        {!isPlatformRole && companyName && (
          <div className="hidden min-w-0 items-center gap-2.5 lg:flex">
            {tenantLogo ? (
              <img src={tenantLogo} alt={companyName} className="h-8 w-8 shrink-0 rounded-lg object-contain" />
            ) : (
              <div
                aria-hidden="true"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-sm font-semibold text-accent-text"
              >
                {companyName.charAt(0).toUpperCase()}
              </div>
            )}
            <span className="max-w-[200px] truncate text-sm font-semibold text-ui-text">{companyName}</span>
          </div>
        )}
      </div>

      {/* Right cluster: theme, page-provided actions slot, notifications, user menu */}
      <div className="flex items-center gap-0.5 md:gap-1.5">
        {/* Theme mode cycle button — sits left of the chat/slot icon */}
        <IconButton label={modeTitle} onClick={cycleMode} aria-label={`Color mode: ${colorMode}`}>
          <ModeIcon size={18} />
        </IconButton>

        <div ref={slotRef} className="flex items-center gap-1" />

        <NotificationBell />

        {/* User menu */}
        <div ref={menuRef} className="relative ml-1">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={open}
            className="flex items-center gap-2.5 rounded-full p-0.5 pr-1 transition-colors hover:bg-ui-hover sm:pr-3 btn-icon-hover"
          >
            <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-accent-soft text-sm font-semibold text-accent-text">
              {me?.profile_picture_url ? (
                <img src={me.profile_picture_url} alt={userName ?? ''} className="h-full w-full object-cover" />
              ) : (
                userName?.charAt(0).toUpperCase()
              )}
            </div>
            <span className="hidden text-sm font-medium text-ui-text sm:block">{userName}</span>
          </button>

          {open && (
            <div
              role="menu"
              className="glass absolute right-0 z-50 mt-2 w-64 overflow-hidden rounded-2xl shadow-pop"
            >
              {/* User info */}
              <div className="border-b border-ui-border px-4 py-3">
                <p className="truncate text-sm font-semibold text-ui-text">{userName}</p>
                {userEmail && <p className="truncate text-xs text-ui-text2">{userEmail}</p>}
              </div>

              {/* Appearance / color mode */}
              <div className="border-b border-ui-border px-4 py-3">
                <p className="mb-2 text-xs font-medium text-ui-text2">{t('colorMode.label')}</p>
                <div className="flex gap-1 rounded-full bg-ui-subtle p-1">
                  {(['light', 'dark', 'system'] as const).map((m) => {
                    const Icon = MODE_ICON[m];
                    return (
                      <button
                        key={m}
                        type="button"
                        role="menuitemradio"
                        aria-checked={colorMode === m}
                        onClick={() => handleSetMode(m)}
                        title={t(`colorMode.${m}`)}
                        className={cn(
                          'flex flex-1 items-center justify-center gap-1.5 rounded-full py-1.5 text-xs font-medium transition-colors',
                          colorMode === m
                            ? 'bg-ui-card text-ui-text shadow-card'
                            : 'text-ui-text2 hover:text-ui-text',
                        )}
                      >
                        <Icon size={14} />
                        <span className="capitalize">{t(`colorMode.${m}`)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Edit profile */}
              <button
                type="button"
                role="menuitem"
                onClick={handleEditProfile}
                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-ui-text hover:bg-ui-hover"
              >
                <UserIcon size={16} className="shrink-0 text-icon" />
                Edit Profile
              </button>

              {/* Logout */}
              <button
                type="button"
                role="menuitem"
                onClick={handleLogout}
                disabled={isLoading}
                className="flex w-full items-center gap-2.5 px-4 py-3 text-left text-sm font-medium text-danger-text hover:bg-ui-hover disabled:opacity-50"
              >
                <LogOutIcon size={16} className="shrink-0" />
                {t('auth.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopBar;
